import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Plus } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronIcon } from '../common/selectIcons';
import { Tooltip } from '../common/Tooltip';
import { CreateBrandModal } from './scenario/CreateBrandModal';
import type { BrandWithUrls } from '../../features/brands/urls';
import styles from './TemplateUrlPicker.module.css';

interface Option {
  value: string;
  label: string;
}

/** ネイティブ select を使わないカスタムドロップダウン。
 *  .nestedSection の overflow:hidden に切られないよう portal で描画する。 */
function CustomSelect({
  value,
  options,
  placeholder,
  disabled,
  onChange,
  ariaLabel,
}: {
  value: string;
  options: Option[];
  placeholder: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  ariaLabel: string;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 0 });
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  }, []);

  useEffect(() => {
    if (!open) return;
    updatePosition();
    const handleClickOutside = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current && !triggerRef.current.contains(target) &&
        menuRef.current && !menuRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setOpen(false); triggerRef.current?.focus(); }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('keydown', handleKey);
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open, updatePosition]);

  const selected = options.find((o) => o.value === value);

  return (
    <div className={styles.selectWrap}>
      <button
        ref={triggerRef}
        type="button"
        className={styles.trigger}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((v) => !v)}
      >
        <span className={`${styles.triggerText} ${selected ? '' : styles.placeholder}`}>
          {selected?.label ?? placeholder}
        </span>
        <span className={styles.arrow}>
          <ChevronIcon open={open} />
        </span>
      </button>
      {open && createPortal(
        <div
          ref={menuRef}
          className={`modal-surface ${styles.menu}`}
          style={{ position: 'fixed', top: pos.top, left: pos.left, minWidth: pos.width }}
          role="listbox"
        >
          {options.length === 0 ? (
            <div className={styles.menuEmpty}>選択肢がありません</div>
          ) : (
            options.map((o) => (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={o.value === value}
                className={`${styles.option} ${o.value === value ? styles.optionSelected : ''}`}
                onClick={() => { onChange(o.value); setOpen(false); }}
              >
                {o.label}
              </button>
            ))
          )}
        </div>,
        document.body,
      )}
    </div>
  );
}

interface TemplateUrlPickerProps {
  brands: BrandWithUrls[];
  /** 現在の参照 URL (主導線の入力欄と共有) */
  value: string;
  onChange: (url: string) => void;
}

/**
 * テンプレートキャプチャの副導線: 登録済みブランド → サイトを選んで URL 欄に反映する。
 * ブランドはカスタムドロップダウン、右の「＋」でブランド追加モーダルを開く。
 */
export function TemplateUrlPicker({ brands, value, onChange }: TemplateUrlPickerProps) {
  const queryClient = useQueryClient();
  const [brandId, setBrandId] = useState('');
  const [createOpen, setCreateOpen] = useState(false);

  // 初期表示: サイトが1つでも登録されていれば、先頭ブランドの先頭サイトを
  // デフォルト選択にして URL 欄も埋める。ユーザー操作と競合しないよう1度だけ。
  const didDefaultRef = useRef(false);
  useEffect(() => {
    if (didDefaultRef.current || brands.length === 0) return;
    didDefaultRef.current = true;
    const current = value.trim();
    if (current) {
      // 既に URL が入っている場合は、その URL を持つブランドを選択状態にする
      const owning = brands.find((b) => b.urls.some((u) => u.url === current));
      if (owning) setBrandId(owning.id);
      return;
    }
    const firstBrand = brands[0];
    const firstSite = firstBrand.urls[0];
    if (firstSite) {
      setBrandId(firstBrand.id);
      onChange(firstSite.url);
    }
  }, [brands, value, onChange]);

  const selectedBrand = brands.find((b) => b.id === brandId);
  // サイトの選択状態は value から導出する (生成成功時の URL クリアと自然に同期)
  const selectedSiteId = selectedBrand?.urls.find((u) => u.url === value.trim())?.id ?? '';

  return (
    <>
      <div className={styles.row}>
        <CustomSelect
          ariaLabel="ブランドから選択"
          placeholder="ブランド"
          value={brandId}
          options={brands.map((b) => ({ value: b.id, label: b.brand_name }))}
          onChange={setBrandId}
        />
        <CustomSelect
          ariaLabel="登録済みサイトを選択"
          placeholder="サイト"
          value={selectedSiteId}
          disabled={!selectedBrand}
          options={(selectedBrand?.urls ?? []).map((u) => ({ value: u.id, label: u.name }))}
          onChange={(siteId) => {
            const site = selectedBrand?.urls.find((u) => u.id === siteId);
            if (site) onChange(site.url);
          }}
        />
        <Tooltip content="ブランドを追加する">
          <button
            type="button"
            className={styles.addButton}
            aria-label="ブランドを追加する"
            onClick={() => setCreateOpen(true)}
          >
            <Plus size={16} aria-hidden="true" />
          </button>
        </Tooltip>
      </div>

      <CreateBrandModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onBrandCreated={() => {
          // 新規ブランドは URL 未登録なので一覧には即時反映されないが、
          // 既存キャッシュを最新化しておく (URL 追加後に選択可能になる)
          queryClient.invalidateQueries({ queryKey: ['all-brand-urls'] });
        }}
      />
    </>
  );
}
